import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getPurchaseOrders } from '../api';
import { PurchaseOrder, PurchaseOrderItem } from '../types/purchaseOrder';
import { formatDate, formatCurrency } from '../utils';

interface PurchaseOrderDetailsDialogProps {
  visible: boolean;
  onHide: () => void;
  poNumber: string;
}

const PurchaseOrderDetailsDialog: React.FC<PurchaseOrderDetailsDialogProps> = ({
  visible,
  onHide,
  poNumber
}) => {
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPurchaseOrder = async () => {
      if (!visible || !poNumber) return;
      
      setLoading(true);
      try {
        const [orders] = await getPurchaseOrders({
          page: 1,
          limit: 1,
          poNumber: poNumber
        });
        if (orders.length > 0) {
          setPurchaseOrder(orders[0]);
        } else {
          console.error('Purchase order not found:', poNumber);
        }
      } catch (error) {
        console.error('Failed to fetch purchase order:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPurchaseOrder();
  }, [visible, poNumber]);

  const calculateItemSubtotal = (item: PurchaseOrderItem): number => {
    return parseFloat(item.quantity.toString()) * parseFloat(item.unitPrice.toString());
  };

  const calculateTotalPrice = (items: PurchaseOrderItem[]): number => {
    return items.reduce((total, item) => total + calculateItemSubtotal(item), 0);
  };

  const handlingUnitsTemplate = (item: PurchaseOrderItem) => {
    if (!item.handlingUnits || item.handlingUnits.length === 0) {
      return <span>No handling units</span>;
    }
    return (
      <div>
        {item.handlingUnits.map((hu) => (
          <div key={hu.id} className="mb-2">
            <Tag 
              value={`${hu.huNumber} - ${hu.stage}`}
              severity={hu.stage === 'Completed' ? 'success' : 'info'}
            />
          </div>
        ))}
      </div>
    );
  };

  if (!purchaseOrder && loading) {
    return (
      <Dialog
        visible={visible}
        onHide={onHide}
        style={{ width: '90vw', maxWidth: '1200px' }}
        modal
        header="Loading Purchase Order Details..."
      >
        <div className="flex justify-content-center align-items-center" style={{ height: '200px' }}>
          <ProgressSpinner />
        </div>
      </Dialog>
    );
  }

  if (!purchaseOrder) return null;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      style={{ width: '90vw', maxWidth: '1200px' }}
      modal
      header={`Purchase Order Details - ${purchaseOrder.poNumber}`}
    >
      <div className="grid">
        <div className="col-12 md:col-6">
          <div className="p-2 surface-100 border-round mb-3">
            <h3 className="mt-0 mb-3">Basic Information</h3>
            <div className="grid">
              <div className="col-12 mb-2">
                <label className="font-bold block mb-1">PO Number:</label>
                <span>{purchaseOrder.poNumber}</span>
              </div>
              <div className="col-12 mb-2">
                <label className="font-bold block mb-1">Client:</label>
                <span>{purchaseOrder.client?.name || 'N/A'}</span>
              </div>
              <div className="col-12 mb-2">
                <label className="font-bold block mb-1">Status:</label>
                <Tag value={purchaseOrder.status} severity={
                  purchaseOrder.status === 'Completed' ? 'success' :
                  purchaseOrder.status === 'Rejected' ? 'danger' :
                  purchaseOrder.status === 'Approved' ? 'info' : 'warning'
                } />
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 md:col-6">
          <div className="p-2 surface-100 border-round mb-3">
            <h3 className="mt-0 mb-3">Dates & Details</h3>
            <div className="grid">
              <div className="col-12 mb-2">
                <label className="font-bold block mb-1">Created:</label>
                <span>{formatDate(new Date(purchaseOrder.createdAt))}</span>
              </div>
              <div className="col-12 mb-2">
                <label className="font-bold block mb-1">Last Updated:</label>
                <span>{formatDate(new Date(purchaseOrder.updatedAt))}</span>
              </div>
              <div className="col-12 mb-2">
                <label className="font-bold block mb-1">Total Value:</label>
                <span className="text-xl font-bold">
                  {formatCurrency(calculateTotalPrice(purchaseOrder.items))}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <h3>Items</h3>
          <DataTable value={purchaseOrder.items} className="mb-3" stripedRows>
            <Column field="itemNumber" header="Item #" />
            <Column 
              field="product.name" 
              header="Product" 
              body={(item: PurchaseOrderItem) => item.product?.name || 'N/A'}
            />
            <Column field="quantity" header="Quantity" />
            <Column 
              field="unitPrice" 
              header="Unit Price" 
              body={(item: PurchaseOrderItem) => formatCurrency(parseFloat(item.unitPrice.toString()))}
            />
            <Column field="unit" header="Unit" />
            <Column field="shipToParty" header="Ship To" />
            <Column field="batchNumber" header="Batch" />
            <Column 
              field="deliveryDate" 
              header="Delivery Date" 
              body={(item: PurchaseOrderItem) => formatDate(new Date(item.deliveryDate))}
            />
            <Column 
              field="handlingUnits" 
              header="Handling Units"
              body={handlingUnitsTemplate}
            />
          </DataTable>
        </div>

        {purchaseOrder.notes && (
          <div className="col-12">
            <h3>Notes</h3>
            <p className="white-space-pre-line">{purchaseOrder.notes}</p>
          </div>
        )}

        {purchaseOrder.history && purchaseOrder.history.length > 0 && (
          <div className="col-12">
            <h3>History</h3>
            <div className="surface-50 border-round p-3">
              {purchaseOrder.history.map((entry, index) => (
                <div key={index} className="mb-3 p-3 surface-100 border-round">
                  <div className="text-sm text-500">
                    {formatDate(new Date(entry.timestamp))}
                  </div>
                  <div className="font-medium">{entry.action}</div>
                  <div>by {entry.user}</div>
                  {entry.details && (
                    <div className="text-sm mt-2 text-600">{entry.details}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default PurchaseOrderDetailsDialog;
